/**
 * useLocalSyntaxErrorEntries — the standalone, debounced LOCAL tree-sitter
 * parse-error producer (VQE Polish, EDG-11..15). Runs the per-dialect
 * tree-sitter recovery over THIS tab's buffer and exposes the raw ERROR +
 * MISSING recovery spans (absolute UTF-16 offsets) so the compact affordance
 * can drive its never-silent chip + `vqe-parse` squiggle for the four engines
 * with no language-service syntax marker (mysql / oracle / sqlite — and
 * mssql / pgsql when their marker pass is silent).
 *
 * PM-C parse centralization: the recovery view is fetched through
 * `getSharedErrorRecoveryView` — the source-keyed memo the `sql-syntax`
 * live-marker pass (SqlMonaco) also consumes — so on an idle buffer the two
 * debounced passes share ONE tree-sitter parse instead of parsing the same
 * model twice at roughly 350/400 ms.
 *
 * ── CPU discipline (Docs/QueryEditor_CpuIssue.md) ──────────────────────────
 * The tree-sitter parse is main-thread but ms-cheap; it must still NOT run
 * un-debounced per keystroke. This hook clones the guard triplet the MSSQL
 * `'sql-syntax'` pass uses (`SqlMonaco.tsx`):
 *   • DEBOUNCE (350 ms) after the last content change — never per keystroke;
 *   • MAX_SOURCE_LEN cap — skip pathologically large buffers entirely;
 *   • model-version guard — capture `model.getVersionId()` before the async
 *     wasm parse and DROP the result if the buffer changed underneath it
 *     (never surface spans computed for stale text).
 * The result feeds only the conservative predicate in `localSyntaxErrorSignal.ts`
 * (tree-sitter overlap AND an unambiguous structural break), so a false ERROR
 * node from tree-sitter alone never lights the chip.
 *
 * Reads the buffer straight off Monaco's model (the same source
 * `getStatementAtCursor` reads in the host), so recovery offsets and the cursor
 * segment's offsets are drawn from one snapshot and align for overlap testing.
 */
import { useEffect, useState } from 'react';
import type { Monaco } from '@monaco-editor/react';

import type { DatabaseEngineId } from '@/core/engines';
import { getSharedErrorRecoveryView } from '../../../intellisense/diagnostics/sharedRecoveryView';
import type { LocalRecoveryEntry } from './localSyntaxErrorSignal';

const DEBOUNCE_MS = 350;
const MAX_SOURCE_LEN = 200_000;

function findOwnModel(monacoInstance: Monaco, container: HTMLElement | null) {
  if (!container) return null;
  const editor = monacoInstance.editor.getEditors().find((candidate) => {
    const node = candidate.getDomNode?.();
    return !!node && container.contains(node);
  });
  return editor?.getModel?.() ?? null;
}

/**
 * @param monacoInstance the page-wide Monaco (from `useMonaco()`), or null before ready.
 * @param containerRef   DOM anchor scoped to THIS tab's Monaco mount — identifies
 *                        the tab's own model among every open tab's.
 * @param engineId       the tab's engine; `mock` and unknown engines yield `null`
 *                        (no grammar fork) — the producer then stays empty.
 * @returns the current recovery spans for the buffer (empty until the first
 *          parse lands, or when the buffer is clean / too large / mock).
 */
export function useLocalSyntaxErrorEntries(
  monacoInstance: Monaco | null,
  containerRef: React.RefObject<HTMLElement | null>,
  engineId: DatabaseEngineId,
): readonly LocalRecoveryEntry[] {
  const [entries, setEntries] = useState<readonly LocalRecoveryEntry[]>([]);

  useEffect(() => {
    if (!monacoInstance) return;
    // Engines with no tree-sitter fork have no local signal — stay empty.
    if (engineId === 'mock') {
      setEntries([]);
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let contentDisposable: { dispose: () => void } | undefined;
    let attachRetries = 40; // ~4 s of 100 ms retries while the editor mounts

    const recompute = async (): Promise<void> => {
      const model = findOwnModel(monacoInstance, containerRef.current);
      if (!model) return;
      const source = model.getValue();
      if (source.length === 0 || source.length > MAX_SOURCE_LEN) {
        if (!cancelled) setEntries([]);
        return;
      }
      const versionAtStart = model.getVersionId();
      const view = await getSharedErrorRecoveryView(engineId, source);
      if (cancelled) return;
      // Stale-guard: drop spans computed for text the user has since edited.
      if (findOwnModel(monacoInstance, containerRef.current) !== model) return;
      if (model.getVersionId() !== versionAtStart) return;
      setEntries(view ?? []);
    };

    const schedule = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void recompute();
      }, DEBOUNCE_MS);
    };

    // The Monaco model can mount a beat after this effect runs; retry attaching
    // the content listener on a bounded interval, then run the initial parse.
    const attach = (): void => {
      if (cancelled) return;
      const model = findOwnModel(monacoInstance, containerRef.current);
      if (!model) {
        attachRetries -= 1;
        if (attachRetries <= 0) return;
        timer = setTimeout(attach, 100);
        return;
      }
      contentDisposable = model.onDidChangeContent(schedule);
      void recompute();
    };

    attach();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      contentDisposable?.dispose();
    };
  }, [monacoInstance, containerRef, engineId]);

  return entries;
}
